'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import type { PublicShareManifest, SnapshotItem } from '@containd/shared';
import {
  parseLinkTarget,
  resolveWebSharePolicies,
  shareItemPathWithFocus,
  shouldConfirmExternalNavigation,
} from '@containd/shared';
import { usePublicUi } from '@/lib/publicUi';
import {
  GenericItemHost,
  ItemCardHost,
  runDescriptorPipeline,
  snapshotItemToPipelineItem,
  type RenderDescriptor,
} from '@containd/template-render';
import {
  DEMO_CONTAINER_ELEMENT_ID,
  DEMO_ITEM_ELEMENT_ID,
} from '@containd/template-render';
import {
  canEnterShareItem,
  isGenericSnapshotItem,
  resolveSelectedTapDestination,
  shouldOpenLinkOutOnActivate,
  shouldShowEnterHandle,
  shouldShowLayoutControls,
  shouldShowSelectionHandles,
} from '@/lib/viewingTap';
import { resolvePlaceholderOriginal } from '@/lib/shareNavigation';
import { contrastTextForBg } from '@/lib/tagPillContrast';
import {
  bakedDescriptorsFor,
  edgeToSvgPath,
  edgesForCanvas,
  paintRequiresPipelineFallback,
  publishedLayoutsForItem,
  publishedLayoutSlot,
  resolveFrozenViewSettings,
  resolveSharePaintColors,
  shouldUseGenericSurface,
} from '@/lib/freezePaint';
import { resolveBakedDescriptorsMediaUrls } from '@/lib/resolveBakedDescriptorsMediaUrls';
import { resolveItemFaceMediaId } from '@/lib/shareFaceMedia';
import {
  preferCanvasThumbForItem,
  resolveShareCanvasRasterUrl,
} from '@/lib/shareCanvasThumb';
import {
  canvasPageBorderOverlayStyle,
  canvasPageStageBoxStyle,
} from '@/lib/canvasPageChrome';
import {
  buildCapGeometry,
  dashArrayForStyle,
  GRAPH_EDGE_LABEL_FONT_FAMILY,
  GRAPH_EDGE_LABEL_SIZE,
  labelPillFill,
  labelPositionOnPath,
  labelsForFrozenEdge,
  offsetPathEndpoints,
  parsePathTangents,
  pathEndpoints,
  resolvedCaps,
} from '@/lib/graphEdgePaint';
import { LayoutControlsOverlay } from './LayoutControlsOverlay';
import { SelectionActionHandles } from './SelectionActionHandles';
import { ExternalLinkConfirmDialog } from './ExternalLinkConfirmDialog';
import { CanvasSvgBackground } from './CanvasSvgBackground';
import styles from './ViewingCanvas.module.css';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 1.2;
const FOCUS_PADDING = 40;
/** Movement before a 1-finger press becomes a canvas pan (keeps short taps interactive). */
const PAN_THRESHOLD_PX = 8;
/** Swallow residual clicks after pan/pinch; long enough for delayed iOS clicks. */
const SUPPRESS_CLICK_MS = 300;
const CANVAS_GESTURE_CHROME =
  '[data-canvas-controls], [data-layout-controls], [data-external-link-dialog]';
/** Match Containd DraggableSquare `selectedOutline` borderWidth. */
const SELECTION_FRAME = 3;
const SELECTION_COLOR = '#0a7ea4';
/** Match Containd ItemTagPillRow compact pill estimate. */
const TAG_PILL_EST_WIDTH = 32;
const TAG_PILL_PRIMARY_FALLBACK = '#0a7ea4';

type Rect = { x: number; y: number; w: number; h: number };



function estimateCanvasTagMaxVisible(
  width: number,
  borderRadius = 0,
): number {
  const cornerInset = Math.min(borderRadius * 0.5, width * 0.22);
  const usableWidth = Math.max(0, width - 16 - cornerInset * 2);
  return Math.min(3, Math.max(1, Math.floor(usableWidth / TAG_PILL_EST_WIDTH)));
}

function canvasTagOverlayStyle(
  width: number,
  height: number,
  borderRadius: number,
): CSSProperties {
  const cornerInset = Math.max(
    0,
    Math.min(borderRadius * 0.5, width * 0.22, height * 0.22),
  );
  const bottomInset = Math.max(5, Math.min(cornerInset * 0.85, height * 0.14));
  const horizontalInset = Math.max(4, cornerInset);
  return {
    position: 'absolute',
    bottom: bottomInset,
    left: horizontalInset,
    right: horizontalInset,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    zIndex: 10,
    pointerEvents: 'none',
  };
}

function contentBounds(items: SnapshotItem[], canvasW: number, canvasH: number): Rect {
  if (items.length === 0) {
    return { x: 0, y: 0, w: canvasW, h: canvasH };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const item of items) {
    const g = item.geometry;
    minX = Math.min(minX, g.x);
    minY = Math.min(minY, g.y);
    maxX = Math.max(maxX, g.x + g.width);
    maxY = Math.max(maxY, g.y + g.height);
  }
  return {
    x: minX,
    y: minY,
    w: Math.max(1, maxX - minX),
    h: Math.max(1, maxY - minY),
  };
}

function fitRectInViewport(
  rect: Rect,
  viewportW: number,
  viewportH: number,
  clampZoomFn: (z: number) => number,
  opts?: { padding?: number; allowUpscale?: boolean },
): { zoom: number; pan: { x: number; y: number } } {
  const padding = opts?.padding ?? FOCUS_PADDING;
  const allowUpscale = opts?.allowUpscale ?? false;
  const availW = Math.max(1, viewportW - padding * 2);
  const availH = Math.max(1, viewportH - padding * 2);
  const fit = Math.min(availW / rect.w, availH / rect.h);
  const zoom = clampZoomFn(allowUpscale ? fit : Math.min(fit, 1));
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  return {
    zoom,
    pan: {
      x: viewportW / 2 - cx * zoom,
      y: viewportH / 2 - cy * zoom,
    },
  };
}

type LayoutInfo = { id?: string; displayName?: unknown; renderAsBuiltin?: boolean };

function resolveElement(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): { layouts: LayoutInfo[] } | undefined {
  const doc = manifest.templateDocuments?.[item.templateId] as
    | {
        elements?: Array<{
          id?: string;
          layouts?: LayoutInfo[];
        }>;
      }
    | undefined;
  if (!doc?.elements?.length) return undefined;
  const el =
    doc.elements.find((e) => e.id === item.templateElementId) ??
    doc.elements[0];
  return el ? { layouts: el.layouts ?? [] } : undefined;
}

function layoutMeta(manifest: PublicShareManifest, item: SnapshotItem): {
  total: number;
  labels: string[];
} {
  if (isGenericSnapshotItem(item)) {
    return { total: 1, labels: [] };
  }
  const published = publishedLayoutsForItem(manifest, item);
  if (published.length > 1 || item.publishedLayouts?.length) {
    return {
      total: published.length,
      labels: published.map(
        (l, i) => l.label?.trim() || `Layout ${l.index + 1 || i + 1}`,
      ),
    };
  }
  // Legacy: TemplateDocument layouts (freezeVersion 0 demos only).
  const el = resolveElement(manifest, item);
  const layouts = el?.layouts ?? [];
  if (layouts.length === 0) return { total: 1, labels: [] };
  const labels = layouts.map((layout, i) => {
    const dn = layout.displayName;
    if (typeof dn === 'string' && dn.trim()) return dn;
    if (dn && typeof dn === 'object') {
      const rec = dn as Record<string, string>;
      return rec.en || rec[Object.keys(rec)[0] ?? ''] || `Layout ${i + 1}`;
    }
    return layout.id || `Layout ${i + 1}`;
  });
  return { total: layouts.length, labels };
}

function elementIdFor(templateId: string, item?: SnapshotItem): string {
  if (item?.templateElementId) return item.templateElementId;
  if (templateId.includes('container')) return DEMO_CONTAINER_ELEMENT_ID;
  if (templateId.includes('smoke')) return 'el-v2-smoke-primary';
  return DEMO_ITEM_ELEMENT_ID;
}

function canvasDevicePixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  const dpr = window.devicePixelRatio;
  return Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
}

function resolveItemImageUrl(
  manifest: PublicShareManifest,
  item: SnapshotItem,
  zoomLevel: number,
  devicePixelRatio: number,
): string | null {
  let mediaId = resolveItemFaceMediaId(item);
  let geometryItem = item;
  // Belt-and-suspenders: seat may lack aliased media on older freezes — use original.
  if (!mediaId && item.isPlaceholder === true) {
    const uuid = item.originalItemUuid?.trim();
    const original = uuid
      ? manifest.items.find((i) => i.uuid === uuid)
      : undefined;
    if (original) {
      mediaId = resolveItemFaceMediaId(original);
      geometryItem = original;
    }
  }
  if (!mediaId) return null;
  return resolveShareCanvasRasterUrl(manifest, mediaId, {
    animateImages: item.animateImages === true,
    preferThumb: preferCanvasThumbForItem(
      geometryItem.geometry.width,
      geometryItem.geometry.height,
      zoomLevel,
      devicePixelRatio,
    ),
  });
}

function paintDescriptors(
  manifest: PublicShareManifest,
  item: SnapshotItem,
  layoutIndex: number,
  preferThumb: boolean,
): RenderDescriptor[] {
  const rasterOpts = {
    animateImages: item.animateImages === true,
    preferThumb,
  };
  const baked = bakedDescriptorsFor(manifest, item.uuid, layoutIndex);
  if (baked) {
    return resolveBakedDescriptorsMediaUrls(manifest, baked, rasterOpts);
  }
  // Host-resolved shares must not re-run expressions — unknown templates → empty → generic.
  if (!paintRequiresPipelineFallback(manifest)) return [];
  const template = manifest.templateDocuments?.[item.templateId];
  if (!template) return [];
  const pipelineItem = snapshotItemToPipelineItem(item);
  pipelineItem.layoutInUse = layoutIndex;
  const faceId = resolveItemFaceMediaId(item);
  const faceUrl = faceId
    ? resolveShareCanvasRasterUrl(manifest, faceId, rasterOpts)
    : null;
  if (faceUrl) pipelineItem.imageUris = [faceUrl];
  try {
    const result = runDescriptorPipeline(template as never, pipelineItem, {
      elementId: elementIdFor(item.templateId, item),
      layoutIndex,
    });
    return resolveBakedDescriptorsMediaUrls(
      manifest,
      result.descriptors ?? [],
      rasterOpts,
    );
  } catch {
    return [];
  }
}

export interface ViewingCanvasProps {
  manifest: PublicShareManifest;
  items: SnapshotItem[];
  /** Current canvas parent (null = outer). Used to select frozen graph edges. */
  canvasParentUuid?: string | null;
  canvasWidth: number;
  canvasHeight: number;
  /** Canvas page fill (frozen palette `surface`). */
  backgroundColor: string;
  /** Letterbox / empty space around the canvas (frozen palette `background`). */
  viewportBackgroundColor?: string;
  /** Outside-stroke page frame color (Containd canvas border overlay). */
  canvasBorderColor?: string;
  /** Frozen palette item defaults when geometry omits shell colors. */
  itemDefaultBackground?: string;
  itemDefaultBorder?: string;
  itemDefaultText?: string;
  itemDefaultDropShadow?: string;
  svgBackgroundUrl?: string | null;
  layoutOverride: Record<string, number>;
  onCycleLayout: (item: SnapshotItem, delta: number) => void;
  onEnterContainer: (item: SnapshotItem) => void;
  onOpenItem: (item: SnapshotItem) => void;
  /** Jump to the original’s canvas from a linked placeholder seat. */
  onRevealItem?: (placeholder: SnapshotItem) => void;
  /** Select this item after a canvas visit (placeholder jump / Back). */
  focusItemUuid?: string | null;
}

export function ViewingCanvas({
  manifest,
  items,
  canvasParentUuid = null,
  canvasWidth,
  canvasHeight,
  backgroundColor,
  viewportBackgroundColor,
  canvasBorderColor,
  itemDefaultBackground = '#ffffff',
  itemDefaultBorder = '#d0d0d0',
  itemDefaultText = '#ffffff',
  itemDefaultDropShadow = '#000000',
  svgBackgroundUrl,
  layoutOverride,
  onCycleLayout,
  onEnterContainer,
  onOpenItem,
  onRevealItem,
  focusItemUuid = null,
}: ViewingCanvasProps) {
  const { extra } = usePublicUi();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const selectedUuidRef = useRef<string | null>(null);
  const activateGuardRef = useRef(0);
  const [pendingExternal, setPendingExternal] = useState<{
    href: string;
    openInNewTab: boolean;
  } | null>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  /** Source of truth during gestures — never clobber from React state mid-wheel. */
  const viewRef = useRef({ x: 0, y: 0, zoom: 1 });
  const wheelAnchorRef = useRef<{
    canvasX: number;
    canvasY: number;
  } | null>(null);
  const wheelAnchorClearRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const rafFlushRef = useRef<number | null>(null);
  const didInitialFocus = useRef(false);
  const drag = useRef<{
    ox: number;
    oy: number;
    px: number;
    py: number;
    moved: boolean;
    startedOnCard: boolean;
  } | null>(null);
  const pinch = useRef<{
    distance: number;
    zoom: number;
    canvasX: number;
    canvasY: number;
  } | null>(null);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  /** Swallow clicks until this time (ms, performance.now) after a committed pan/pinch. */
  const suppressClickUntilRef = useRef(0);

  const armSuppressClick = () => {
    suppressClickUntilRef.current = performance.now() + SUPPRESS_CLICK_MS;
  };

  const childCountByParent = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of manifest.items) {
      if (!item.parentUuid) continue;
      map.set(item.parentUuid, (map.get(item.parentUuid) ?? 0) + 1);
    }
    return map;
  }, [manifest.items]);

  const viewSettings = useMemo(
    () => resolveFrozenViewSettings(manifest),
    [manifest],
  );

  useEffect(() => {
    if (focusItemUuid) {
      selectedUuidRef.current = focusItemUuid;
      setSelectedUuid(focusItemUuid);
      return;
    }
    selectedUuidRef.current = null;
    setSelectedUuid(null);
  }, [canvasParentUuid, focusItemUuid]);

  const graphEdges = useMemo(
    () => edgesForCanvas(manifest, canvasParentUuid),
    [manifest, canvasParentUuid],
  );

  const clampZoom = useCallback(
    (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)),
    [],
  );

  const flushViewToReact = useCallback(() => {
    if (rafFlushRef.current != null) return;
    rafFlushRef.current = requestAnimationFrame(() => {
      rafFlushRef.current = null;
      const v = viewRef.current;
      setPan({ x: v.x, y: v.y });
      setZoom(v.zoom);
    });
  }, []);

  const setView = useCallback(
    (next: { x: number; y: number; zoom: number }) => {
      viewRef.current = next;
      flushViewToReact();
    },
    [flushViewToReact],
  );

  /**
   * Zoom so the canvas point under the pointer stays fixed.
   * World-point is locked for the wheel gesture to avoid FP / React drift.
   */
  const zoomAtClient = useCallback(
    (
      clientX: number,
      clientY: number,
      nextZoomRaw: number,
      opts?: { lockAnchor?: boolean; anchor?: { canvasX: number; canvasY: number } },
    ) => {
      const el = viewportRef.current;
      if (!el) return;
      const v0 = viewRef.current;
      const z1 = clampZoom(nextZoomRaw);
      if (Math.abs(z1 - v0.zoom) < 1e-9) return;

      const rect = el.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;

      let anchor = opts?.anchor ?? null;
      if (opts?.lockAnchor) {
        if (!wheelAnchorRef.current) {
          wheelAnchorRef.current = {
            canvasX: (px - v0.x) / v0.zoom,
            canvasY: (py - v0.y) / v0.zoom,
          };
        }
        anchor = wheelAnchorRef.current;
        if (wheelAnchorClearRef.current) {
          clearTimeout(wheelAnchorClearRef.current);
        }
        wheelAnchorClearRef.current = setTimeout(() => {
          wheelAnchorRef.current = null;
          wheelAnchorClearRef.current = null;
        }, 120);
      }

      const canvasX = anchor?.canvasX ?? (px - v0.x) / v0.zoom;
      const canvasY = anchor?.canvasY ?? (py - v0.y) / v0.zoom;

      setView({
        zoom: z1,
        x: px - canvasX * z1,
        y: py - canvasY * z1,
      });
    },
    [clampZoom, setView],
  );

  const zoomTowardViewportCenter = useCallback(
    (nextZoomRaw: number) => {
      const el = viewportRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      zoomAtClient(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        nextZoomRaw,
      );
    },
    [zoomAtClient],
  );

  const applyFocus = useCallback(
    (mode: 'canvas' | 'content') => {
      const el = viewportRef.current;
      if (!el) return false;
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      if (vw < 8 || vh < 8) return false;
      const rect =
        mode === 'content'
          ? contentBounds(items, canvasWidth, canvasHeight)
          : { x: 0, y: 0, w: canvasWidth, h: canvasHeight };
      const next = fitRectInViewport(rect, vw, vh, clampZoom, {
        allowUpscale: mode === 'content',
      });
      wheelAnchorRef.current = null;
      setView({ x: next.pan.x, y: next.pan.y, zoom: next.zoom });
      return true;
    },
    [items, canvasWidth, canvasHeight, clampZoom, setView],
  );

  useLayoutEffect(() => {
    didInitialFocus.current = false;
    const el = viewportRef.current;
    if (!el) return;

    const run = () => {
      if (applyFocus('canvas')) {
        didInitialFocus.current = true;
      }
    };

    run();
    const ro = new ResizeObserver(() => {
      if (!didInitialFocus.current) run();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [items, canvasWidth, canvasHeight, applyFocus]);

  const painted = useMemo(() => {
    const dpr = canvasDevicePixelRatio();
    return items.map((item) => {
      const layoutIndex = layoutOverride[item.uuid] ?? item.layoutInUse ?? 0;
      const generic = shouldUseGenericSurface(manifest, item, layoutIndex);
      const meta = layoutMeta(manifest, item);
      const published = publishedLayoutsForItem(manifest, item);
      const layoutLabel =
        published.find((l) => l.index === layoutIndex)?.label ??
        meta.labels[layoutIndex];
      const preferThumb = preferCanvasThumbForItem(
        item.geometry.width,
        item.geometry.height,
        zoom,
        dpr,
      );
      const imageUrl = viewSettings.showItemImage
        ? resolveItemImageUrl(manifest, item, zoom, dpr)
        : null;
      return {
        item,
        layoutIndex,
        generic,
        descriptors: generic
          ? []
          : paintDescriptors(manifest, item, layoutIndex, preferThumb),
        layoutTotal: meta.total,
        layoutLabel,
        layoutSlot: publishedLayoutSlot(manifest, item, layoutIndex),
        imageUrl,
        childrenCount: childCountByParent.get(item.uuid) ?? 0,
      };
    });
  }, [
    items,
    manifest,
    layoutOverride,
    childCountByParent,
    zoom,
    viewSettings.showItemImage,
  ]);

  const paintColors = useMemo(
    () => resolveSharePaintColors(manifest),
    [manifest],
  );

  const activateItem = useCallback(
    (item: SnapshotItem) => {
      const now = Date.now();
      if (now - activateGuardRef.current < 400) return;
      activateGuardRef.current = now;
      if (item.isPlaceholder === true) {
        const original = resolvePlaceholderOriginal(manifest, item);
        if (original && onRevealItem) {
          onRevealItem(item);
        }
        return;
      }
      const destination = resolveSelectedTapDestination(manifest, item);
      // Host parity: valid linkOut opens instead of empty viewer / no-op.
      // Nest (`open`) still wins over linkOut.
      if (shouldOpenLinkOutOnActivate(destination, item.linkOut)) {
        const link = parseLinkTarget(item.linkOut);
        if (!link) return;
        const policies = resolveWebSharePolicies(item.webSharePolicies);
        if (link.kind === 'item') {
          const target = manifest.items.find((i) => i.uuid === link.ref);
          if (!target || target.uuid === item.uuid) return;
          // Follow item: to nest/view the target — do not chain the target's linkOut.
          const targetDest = resolveSelectedTapDestination(manifest, target);
          if (targetDest === 'open') {
            onEnterContainer(target);
          } else if (targetDest === 'view') {
            onOpenItem(target);
          }
          return;
        }
        if (shouldConfirmExternalNavigation(link.href)) {
          setPendingExternal({
            href: link.href,
            openInNewTab: policies.openLinksInNewTab,
          });
          return;
        }
        if (policies.openLinksInNewTab) {
          window.open(link.href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.assign(link.href);
        }
        return;
      }
      if (destination === 'none') {
        return;
      }
      if (destination === 'open') {
        onEnterContainer(item);
      } else {
        onOpenItem(item);
      }
    },
    [manifest, onEnterContainer, onOpenItem, onRevealItem],
  );

  const navigateToLinkedItem = useCallback(
    (itemUuid: string) => {
      const target = manifest.items.find((i) => i.uuid === itemUuid);
      if (!target) return;
      activateItem(target);
    },
    [manifest.items, activateItem],
  );

  const itemLinkHref = useCallback(
    (itemUuid: string) => {
      const target = manifest.items.find((i) => i.uuid === itemUuid);
      if (!target) return null;
      return shareItemPathWithFocus(manifest, target);
    },
    [manifest],
  );

  const dismissExternalLinkConfirm = useCallback(() => {
    setPendingExternal(null);
  }, []);

  const confirmExternalLink = useCallback(() => {
    if (!pendingExternal) return;
    const { href, openInNewTab } = pendingExternal;
    setPendingExternal(null);
    if (openInNewTab) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.assign(href);
    }
  }, [pendingExternal]);

  const handleCardTap = useCallback(
    (item: SnapshotItem) => {
      const policies = resolveWebSharePolicies(item.webSharePolicies);
      if (policies.openOnTap) {
        activateItem(item);
        return;
      }
      if (selectedUuidRef.current === item.uuid) {
        activateItem(item);
        return;
      }
      selectedUuidRef.current = item.uuid;
      setSelectedUuid(item.uuid);
    },
    [activateItem],
  );

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Plain wheel zooms (Containd web parity). preventDefault so the page
      // does not scroll; viewport is overflow:hidden anyway.
      e.preventDefault();
      e.stopPropagation();
      const factor = Math.exp(-e.deltaY * 0.0015);
      zoomAtClient(e.clientX, e.clientY, viewRef.current.zoom * factor, {
        lockAnchor: true,
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (wheelAnchorClearRef.current) clearTimeout(wheelAnchorClearRef.current);
      if (rafFlushRef.current != null) cancelAnimationFrame(rafFlushRef.current);
    };
  }, [zoomAtClient]);

  const capturePointer = (
    viewport: HTMLElement,
    pointerId: number,
  ) => {
    try {
      viewport.setPointerCapture(pointerId);
    } catch {
      /* pointer may already be released */
    }
  };

  const beginPinch = (viewport: HTMLElement | null) => {
    const pts = [...pointers.current.values()];
    if (pts.length < 2) return;
    const dx = pts[0]!.x - pts[1]!.x;
    const dy = pts[0]!.y - pts[1]!.y;
    const midX = (pts[0]!.x + pts[1]!.x) / 2;
    const midY = (pts[0]!.y + pts[1]!.y) / 2;
    const rect = viewport?.getBoundingClientRect();
    const px = rect ? midX - rect.left : midX;
    const py = rect ? midY - rect.top : midY;
    const v = viewRef.current;
    pinch.current = {
      distance: Math.hypot(dx, dy) || 1,
      zoom: v.zoom,
      canvasX: (px - v.x) / v.zoom,
      canvasY: (py - v.y) / v.zoom,
    };
    drag.current = null;
    armSuppressClick();
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    if ((e.target as HTMLElement).closest?.(CANVAS_GESTURE_CHROME)) {
      return;
    }
    const startedOnCard = Boolean(
      (e.target as HTMLElement).closest?.('[data-card]'),
    );
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const viewport = e.currentTarget as HTMLElement;

    if (pointers.current.size >= 2) {
      for (const id of pointers.current.keys()) {
        capturePointer(viewport, id);
      }
      beginPinch(viewportRef.current);
      return;
    }

    // Candidate 1-finger pan — capture only after move threshold so taps still click.
    const v = viewRef.current;
    drag.current = {
      ox: e.clientX,
      oy: e.clientY,
      px: v.x,
      py: v.y,
      moved: false,
      startedOnCard,
    };
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      if (!pinch.current) beginPinch(viewportRef.current);
      if (!pinch.current) return;
      const el = viewportRef.current;
      const pts = [...pointers.current.values()];
      const dx = pts[0]!.x - pts[1]!.x;
      const dy = pts[0]!.y - pts[1]!.y;
      const dist = Math.hypot(dx, dy) || 1;
      const midX = (pts[0]!.x + pts[1]!.x) / 2;
      const midY = (pts[0]!.y + pts[1]!.y) / 2;
      const rect = el?.getBoundingClientRect();
      const px = rect ? midX - rect.left : midX;
      const py = rect ? midY - rect.top : midY;
      const z1 = clampZoom(
        pinch.current.zoom * (dist / pinch.current.distance),
      );
      setView({
        zoom: z1,
        x: px - pinch.current.canvasX * z1,
        y: py - pinch.current.canvasY * z1,
      });
      return;
    }

    if (!drag.current) return;
    const dx = e.clientX - drag.current.ox;
    const dy = e.clientY - drag.current.oy;
    if (!drag.current.moved) {
      if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) return;
      drag.current.moved = true;
      armSuppressClick();
      capturePointer(e.currentTarget as HTMLElement, e.pointerId);
    }

    setView({
      zoom: viewRef.current.zoom,
      x: drag.current.px + dx,
      y: drag.current.py + dy,
    });
  };

  const onPointerUp = (e: ReactPointerEvent) => {
    pointers.current.delete(e.pointerId);

    if (pointers.current.size < 2) {
      pinch.current = null;
    }

    if (pointers.current.size === 1) {
      const remaining = [...pointers.current.entries()][0]!;
      const v = viewRef.current;
      drag.current = {
        ox: remaining[1]!.x,
        oy: remaining[1]!.y,
        px: v.x,
        py: v.y,
        moved: true,
        startedOnCard: true,
      };
      armSuppressClick();
      capturePointer(e.currentTarget as HTMLElement, remaining[0]!);
      return;
    }

    if (pointers.current.size === 0) {
      const d = drag.current;
      if (d?.moved || suppressClickUntilRef.current > performance.now()) {
        // Restart window from gesture end so residual clicks are covered.
        armSuppressClick();
      }
      if (d && !d.moved && !d.startedOnCard) {
        setSelectedUuid(null);
        selectedUuidRef.current = null;
      }
      drag.current = null;
    }
  };

  const onClickCapture = (e: ReactMouseEvent) => {
    if (performance.now() >= suppressClickUntilRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressClickUntilRef.current = 0;
  };

  const stageStyle: CSSProperties = canvasPageStageBoxStyle({
    canvasWidth,
    canvasHeight,
    backgroundColor,
    transform: `matrix(${zoom}, 0, 0, ${zoom}, ${pan.x}, ${pan.y})`,
  });
  const pageBorderStyle = canvasPageBorderOverlayStyle(
    canvasBorderColor ?? '#E0E0E0',
  );

  const itemsLayerOpacity = Math.max(
    0,
    Math.min(1, viewSettings.itemsOpacityOtherModes),
  );

  return (
    <div
      ref={viewportRef}
      className={styles.viewport}
      style={
        viewportBackgroundColor
          ? { backgroundColor: viewportBackgroundColor }
          : undefined
      }
      data-show-labels={viewSettings.showItemLabels ? '1' : '0'}
      data-show-graph={viewSettings.showGraphContent ? '1' : '0'}
      data-theme={manifest.theme === 'dark' ? 'dark' : 'light'}
      data-theme-bg={viewportBackgroundColor ?? ''}
      data-canvas-bg={backgroundColor}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      <div className={styles.stage} style={stageStyle}>
        <div
          className={styles.canvasPageBorder}
          style={pageBorderStyle}
          aria-hidden
          data-canvas-page-border="1"
        />
        {viewSettings.showSvgBackground && svgBackgroundUrl ? (
          <CanvasSvgBackground
            url={svgBackgroundUrl}
            clipToCanvas={viewSettings.clipSvgToCanvas !== false}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
          />
        ) : null}

        {graphEdges.length > 0 ? (
          <svg
            className={styles.graphLayer}
            width={canvasWidth}
            height={canvasHeight}
            aria-hidden
          >
            {graphEdges.map((edge) => {
              const d = edgeToSvgPath(edge);
              if (!d) return null;
              const { source, target } = pathEndpoints(d);
              const caps = resolvedCaps(edge);
              const { startInward, endInward } = parsePathTangents(
                d,
                { x: 1, y: 0 },
                { x: -1, y: 0 },
              );
              const startCapGeo = buildCapGeometry(
                source,
                startInward,
                caps.startCap,
                edge.strokeWidth,
              );
              const endCapGeo = buildCapGeometry(
                target,
                endInward,
                caps.endCap,
                edge.strokeWidth,
              );
              const showEnds = viewSettings.showGraphLineEndings !== false;
              const srcOff =
                showEnds && startCapGeo ? (startCapGeo.lineStopOffset ?? 0) : 0;
              const tgtOff =
                showEnds && endCapGeo ? (endCapGeo.lineStopOffset ?? 0) : 0;
              const linePath =
                srcOff > 0 || tgtOff > 0
                  ? offsetPathEndpoints(
                      d,
                      srcOff,
                      startInward,
                      tgtOff,
                      endInward,
                    )
                  : d;
              const dash = dashArrayForStyle(edge.dash, edge.strokeWidth);
              const labels = labelsForFrozenEdge(edge, viewSettings);
              const edgeColor = edge.strokeColor;

              return (
                <g key={edge.id} data-graph-edge={edge.id}>
                  <path
                    d={linePath}
                    fill="none"
                    stroke={edgeColor}
                    strokeWidth={edge.strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={dash}
                  />
                  {showEnds && startCapGeo
                    ? startCapGeo.circleCenter
                      ? (
                          <circle
                            cx={startCapGeo.circleCenter.x}
                            cy={startCapGeo.circleCenter.y}
                            r={startCapGeo.circleRadius ?? 5}
                            fill={
                              startCapGeo.fill
                                ? edgeColor
                                : startCapGeo.closedHollow
                                  ? backgroundColor
                                  : 'none'
                            }
                            stroke={edgeColor}
                            strokeWidth={edge.strokeWidth}
                            data-graph-cap="start"
                          />
                        )
                      : startCapGeo.pathD
                        ? (
                            <path
                              d={startCapGeo.pathD}
                              fill={
                                startCapGeo.fill
                                  ? edgeColor
                                  : startCapGeo.closedHollow
                                    ? backgroundColor
                                    : 'none'
                              }
                              stroke={startCapGeo.fill ? 'none' : edgeColor}
                              strokeWidth={edge.strokeWidth}
                              strokeLinejoin="round"
                              data-graph-cap="start"
                            />
                          )
                        : null
                    : null}
                  {showEnds && endCapGeo
                    ? endCapGeo.circleCenter
                      ? (
                          <circle
                            cx={endCapGeo.circleCenter.x}
                            cy={endCapGeo.circleCenter.y}
                            r={endCapGeo.circleRadius ?? 5}
                            fill={
                              endCapGeo.fill
                                ? edgeColor
                                : endCapGeo.closedHollow
                                  ? backgroundColor
                                  : 'none'
                            }
                            stroke={edgeColor}
                            strokeWidth={edge.strokeWidth}
                            data-graph-cap="end"
                          />
                        )
                      : endCapGeo.pathD
                        ? (
                            <path
                              d={endCapGeo.pathD}
                              fill={
                                endCapGeo.fill
                                  ? edgeColor
                                  : endCapGeo.closedHollow
                                    ? backgroundColor
                                    : 'none'
                              }
                              stroke={endCapGeo.fill ? 'none' : edgeColor}
                              strokeWidth={edge.strokeWidth}
                              strokeLinejoin="round"
                              data-graph-cap="end"
                            />
                          )
                        : null
                    : null}
                  {labels.map((label, li) => {
                    const mid = labelPositionOnPath(
                      d,
                      source,
                      target,
                      label.t,
                    );
                    const labelSize = GRAPH_EDGE_LABEL_SIZE;
                    const offsetY = labelSize + 2;
                    const ty = mid.y + offsetY;
                    const pillPadX = 4;
                    const pillPadY = 2;
                    const pillW =
                      Math.max(8, label.text.length * labelSize * 0.6) +
                      pillPadX * 2;
                    const pillH = labelSize + pillPadY * 2;
                    const pillX = mid.x - pillW / 2;
                    const pillY = ty - labelSize * 0.82;
                    return (
                      <g
                        key={`${edge.id}-lbl-${li}`}
                        data-graph-label={label.placement}
                      >
                        <rect
                          x={pillX}
                          y={pillY}
                          width={pillW}
                          height={pillH}
                          rx={pillH / 2}
                          fill={labelPillFill(edgeColor)}
                        />
                        <text
                          x={mid.x}
                          y={ty}
                          fontSize={labelSize}
                          fontFamily={GRAPH_EDGE_LABEL_FONT_FAMILY}
                          fill={edgeColor}
                          textAnchor="middle"
                          dominantBaseline="alphabetic"
                        >
                          {label.text}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        ) : null}

        <div
          className={styles.itemsLayer}
          style={{ opacity: itemsLayerOpacity }}
        >
        {painted.map(
          ({
            item,
            descriptors,
            layoutTotal,
            layoutLabel,
            layoutSlot,
            generic,
            imageUrl,
            childrenCount,
          }) => {
            const g = item.geometry;
            const selected = selectedUuid === item.uuid;
            const inset = selected ? SELECTION_FRAME : 0;
            const policies = resolveWebSharePolicies(item.webSharePolicies);
            const isShareRoot =
              item.uuid === manifest.rootItemUuid || item.parentUuid === null;
            const destination = resolveSelectedTapDestination(manifest, item);
            const canEnter = canEnterShareItem(manifest, item);
            const showHandles = shouldShowSelectionHandles({
              selected,
              isShareRoot,
              isPlaceholder: item.isPlaceholder === true,
              openOnTap: policies.openOnTap,
              destination,
            });
            const showEnter = shouldShowEnterHandle(showHandles, canEnter);
            const showLayoutControls = shouldShowLayoutControls({
              selected,
              generic: isGenericSnapshotItem(item),
              layoutTotal,
              allowLayoutChange: policies.allowLayoutChange,
              openOnTap: policies.openOnTap,
            });
            const bg = viewSettings.showItemBackground
              ? (g.backgroundColor ??
                  (generic ? itemDefaultBackground : '#ffffff'))
              : 'transparent';
            const frame = viewSettings.showItemFrame
              ? (g.frameThickness ?? 2)
              : 0;
            const border = viewSettings.showItemFrame
              ? (g.borderColor ??
                  (generic ? itemDefaultBorder : '#d0d0d0'))
              : 'transparent';
            const tagNames = (item.tagIds ?? [])
              .map((id) => {
                const entry = manifest.tags.find((t) => t.id === id);
                return entry?.name ?? id;
              })
              .filter(Boolean);
            const showTagPills =
              viewSettings.showItemBadges !== false &&
              tagNames.length > 0 &&
              g.width >= 56 &&
              g.height >= 48;
            const tagMaxVisible = estimateCanvasTagMaxVisible(
              g.width,
              g.borderRadius ?? 8,
            );
            const visibleTags = tagNames.slice(0, tagMaxVisible);
            const tagOverflow = Math.max(
              0,
              tagNames.length - visibleTags.length,
            );
            const pillPrimary =
              manifest.paintContext?.primaryColor ??
              manifest.themeColorPalettes?.[manifest.theme ?? 'light']
                ?.primary ??
              TAG_PILL_PRIMARY_FALLBACK;

            return (
              <div
                key={item.uuid}
                data-card
                className={styles.cardWrap}
                style={{
                  left: g.x,
                  top: g.y,
                  width: g.width,
                  height: g.height,
                  zIndex: (g.zIndex ?? 0) + (selected ? 100 : 0),
                  transform: g.rotationDeg
                    ? `rotate(${g.rotationDeg}deg)`
                    : undefined,
                }}
              >
                {selected ? (
                  <div
                    className={styles.selectedOutline}
                    style={{
                      borderRadius: g.borderRadius ?? 8,
                      borderColor: SELECTION_COLOR,
                    }}
                  />
                ) : null}
                {showHandles ? (
                  <SelectionActionHandles
                    showEye
                    showEnter={showEnter}
                    fillColor={paintColors.primary}
                    onView={() => onOpenItem(item)}
                    onEnter={() => onEnterContainer(item)}
                  />
                ) : null}
                <div
                  className={styles.cardInner}
                  style={{
                    margin: inset,
                    width: g.width - inset * 2,
                    height: g.height - inset * 2,
                    borderRadius: Math.max(
                      0,
                      (g.borderRadius ?? 8) - inset,
                    ),
                  }}
                >
                  {generic ? (
                    <GenericItemHost
                      width={g.width - inset * 2}
                      height={g.height - inset * 2}
                      title={item.title}
                      borderRadius={Math.max(
                        0,
                        (g.borderRadius ?? 8) - inset,
                      )}
                      frameThickness={frame}
                      backgroundColor={bg}
                      borderColor={border}
                      textColor={g.textColor ?? itemDefaultText}
                      fontSize={g.fontSize ?? 14}
                      imageUrl={imageUrl}
                      svgShapeSettings={item.svgShapeSettings}
                      defaultTitleStyle={item.defaultTitleStyle}
                      defaultDropShadow={item.defaultDropShadow}
                      dropShadowFallbackColor={itemDefaultDropShadow}
                      childrenCount={childrenCount}
                      showItemLabels={viewSettings.showItemLabels}
                      showItemChildrenCount={
                        viewSettings.showItemChildrenCount
                      }
                      isPlaceholder={item.isPlaceholder === true}
                      placeholderLinked={
                        resolvePlaceholderOriginal(manifest, item) != null
                      }
                      linkOut={item.linkOut}
                      onItemLinkNavigate={navigateToLinkedItem}
                      openLinksInNewTab={policies.openLinksInNewTab}
                      itemLinkHref={itemLinkHref}
                      onExternalLinkOpen={(href) =>
                        setPendingExternal({
                          href,
                          openInNewTab: policies.openLinksInNewTab,
                        })
                      }
                      onClick={() => handleCardTap(item)}
                    />
                  ) : (
                    <ItemCardHost
                      width={g.width - inset * 2}
                      height={g.height - inset * 2}
                      borderRadius={Math.max(
                        0,
                        (g.borderRadius ?? 8) - inset,
                      )}
                      frameThickness={frame}
                      backgroundColor={bg}
                      borderColor={border}
                      descriptors={descriptors}
                      defaultDropShadow={item.defaultDropShadow}
                      dropShadowFallbackColor={itemDefaultDropShadow}
                      isPlaceholder={item.isPlaceholder === true}
                      placeholderLinked={
                        resolvePlaceholderOriginal(manifest, item) != null
                      }
                      paintContext={{
                        locale: manifest.paintContext?.locale ?? manifest.locale,
                        primaryColor:
                          manifest.paintContext?.primaryColor ??
                          manifest.themeColorPalettes?.[manifest.theme ?? 'light']
                            ?.primary ??
                          '#0a7ea4',
                        textColor:
                          manifest.paintContext?.textColor ??
                          manifest.themeColorPalettes?.[manifest.theme ?? 'light']
                            ?.text ??
                          itemDefaultText,
                        mutedColor: manifest.paintContext?.mutedColor,
                        ratingColor: manifest.paintContext?.ratingColor,
                        ratingEmptyColor: manifest.paintContext?.ratingEmptyColor,
                        dateLocale: manifest.paintContext?.dateLocale,
                        hour12: manifest.paintContext?.hour12,
                        onItemLinkNavigate: navigateToLinkedItem,
                        openLinksInNewTab: policies.openLinksInNewTab,
                        itemLinkHref,
                        onExternalLinkOpen: (href) =>
                          setPendingExternal({
                            href,
                            openInNewTab: policies.openLinksInNewTab,
                          }),
                      }}
                      onClick={() => handleCardTap(item)}
                    />
                  )}
                  {showTagPills ? (
                    <div
                      className={styles.tagClip}
                      style={{ borderRadius: g.borderRadius ?? 8 }}
                    >
                      <div
                        style={canvasTagOverlayStyle(
                          g.width,
                          g.height,
                          g.borderRadius ?? 8,
                        )}
                      >
                        <div className={styles.tagPillRow}>
                          {visibleTags.map((name) => {
                            const entry = manifest.tags.find(
                              (t) => t.name === name || t.id === name,
                            );
                            const bgColor =
                              entry?.color ?? pillPrimary;
                            return (
                              <span
                                key={name}
                                className={styles.tagPill}
                                style={{
                                  backgroundColor: bgColor,
                                  color: contrastTextForBg(bgColor),
                                }}
                              >
                                {name}
                              </span>
                            );
                          })}
                          {tagOverflow > 0 ? (
                            <span
                              className={`${styles.tagPill} ${styles.tagPillOverflow}`}
                            >
                              +{tagOverflow}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                {showLayoutControls ? (
                  <LayoutControlsOverlay
                    layoutIndex={layoutSlot}
                    totalLayouts={layoutTotal}
                    layoutLabel={layoutLabel}
                    accentColor={paintColors.primary}
                    chipBackground={paintColors.surface}
                    chipText={paintColors.text}
                    chipBorder={paintColors.canvasBorder}
                    onPrev={() => onCycleLayout(item, -1)}
                    onNext={() => onCycleLayout(item, 1)}
                  />
                ) : null}
              </div>
            );
          },
        )}
        </div>
      </div>

      <div className={styles.controlsStack} data-canvas-controls>
        <div
          className={styles.controls}
          role="toolbar"
          aria-label={extra.shareZoom}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() =>
              zoomTowardViewportCenter(viewRef.current.zoom / ZOOM_STEP)
            }
            aria-label={extra.shareZoomOut}
          >
            −
          </button>
          <button
            type="button"
            onClick={() => applyFocus('canvas')}
            aria-label={extra.shareResetView}
            className={styles.zoomLabel}
            title={extra.shareResetView}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            onClick={() =>
              zoomTowardViewportCenter(viewRef.current.zoom * ZOOM_STEP)
            }
            aria-label={extra.shareZoomIn}
          >
            +
          </button>
          <button
            type="button"
            onClick={() => applyFocus('content')}
            aria-label={extra.shareRefocus}
            title={extra.shareRefocus}
            className={styles.focusBtn}
          >
            ⌖
          </button>
        </div>
        <a
          className={styles.publishedWith}
          href="https://containd.space/"
          target="_blank"
          rel="noopener noreferrer"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {extra.sharePublishedWith}{' '}
          <img
            className={styles.publishedWithIcon}
            src="https://containd.space/_assets/brand/containdLogoSmall.png"
            alt=""
            width={12}
            height={12}
          />{' '}
          <strong>Containd</strong>
        </a>
      </div>

      {pendingExternal ? (
        <ExternalLinkConfirmDialog
          href={pendingExternal.href}
          openInNewTab={pendingExternal.openInNewTab}
          onCancel={dismissExternalLinkConfirm}
          onContinue={confirmExternalLink}
        />
      ) : null}
    </div>
  );
}
